/**
 * Preserve `?next=<path>` across the Google OAuth round-trip.
 *
 * Why this exists
 * ---------------
 * When a user deep-links to a protected route (e.g. /admin-dashboard or
 * /parent-corner), PrelaunchGate / ProtectedRoute bounces them to
 * /login?next=%2Fadmin-dashboard. The email-password login flow already
 * honors `?next=`. The Google OAuth flow, however, redirects the browser to
 * accounts.google.com and then back to /auth/google?code=...&state=..., a
 * full-page round-trip that wipes any in-memory state and the original URL.
 *
 * We stash the encoded `next` path in sessionStorage right before kicking
 * off the OAuth redirect, then read + clear it inside AuthCallback once
 * sign-in succeeds. sessionStorage survives the round-trip because the
 * redirect lands back on the same origin.
 *
 * Open-redirect protection: only same-origin paths starting with "/" (and
 * not "//") are honored, exactly the same guard used by LoginPage.
 */

const KEY = "isaiah_oauth_next";

/**
 * Capture the current page's ?next= query (if any) and stash it for the
 * Google OAuth callback to consume. Safe to call unconditionally, it's a
 * no-op when there's no `next` parameter.
 */
export function stashNextForOAuth() {
  try {
    const params = new URLSearchParams(window.location.search);
    const next = params.get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) {
      sessionStorage.setItem(KEY, next);
    }
  } catch {
    /* sessionStorage may be unavailable (private mode, etc.) */
  }
}

/**
 * Read + clear the stashed `next` path. Returns null when nothing was
 * stashed or the value isn't a safe same-origin path.
 */
export function consumeOAuthNext() {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/") && !decoded.startsWith("//")) {
      return decoded;
    }
  } catch {
    /* sessionStorage may be unavailable */
  }
  return null;
}
